import React, { useEffect, useState } from "react";
import { ArrowLeft, Check, Crown, Loader2, RefreshCw, Zap } from "lucide-react";
import { usePlan } from "../context/PlanContext";
import { PLAN_META } from "../lib/plans";
import { createCheckout, changePlan, resumeSubscription } from "../lib/api";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import { toast } from "sonner";

const FEATURES = {
  free: ["3 orçamentos por mês", "1 empresa", "1 modelo A4", "Marca Basiq no PDF"],
  standard: ["20 orçamentos por mês", "3 empresas", "5 modelos premium", "Personalização completa", "WhatsApp", "Sem marca Basiq"],
  pro: ["Orçamentos ilimitados", "Empresas ilimitadas", "Todos os 10 modelos", "Google Drive + e-mail", "Suporte prioritário"],
};

const fmtDate = (sec) => {
  if (!sec) return "";
  try { return new Date(sec * 1000).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; }
};

export default function SubscriptionPage({ onBack }) {
  const { plan, limits, usage, subscription, refresh } = usePlan();
  const sub = subscription || {};
  const currentLookup = sub.lookup_key || null;
  const hasSub = !!sub.has_stripe_subscription && ["active", "trialing"].includes(sub.status);
  const [annual, setAnnual] = useState(currentLookup ? currentLookup.endsWith("yearly") : true);
  const [busy, setBusy] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const statusLine = (() => {
    if (plan === "free") return "Você está no plano gratuito.";
    if (sub.cancel_at_period_end && sub.current_period_end) return `Acesso até ${fmtDate(sub.current_period_end)} — sem renovação.`;
    if (sub.status === "trialing" && sub.trial_end) return `Teste grátis até ${fmtDate(sub.trial_end)}.`;
    if (sub.current_period_end) return `Renova em ${fmtDate(sub.current_period_end)}.`;
    return "Assinatura ativa.";
  })();

  const choose = async (planKey) => {
    const meta = PLAN_META[planKey];
    const lookup = annual ? meta.yearly_lookup : meta.monthly_lookup;
    setBusy(lookup);
    try {
      if (hasSub) {
        const price = annual ? meta.yearly_total : meta.monthly;
        if (!window.confirm(`Trocar para Basiq ${meta.name} (${annual ? "anual" : "mensal"})?\n\nSeu plano atual será encerrado agora e R$ ${price} será cobrado imediatamente no cartão cadastrado, iniciando um novo ciclo.`)) { setBusy(""); return; }
        const r = await changePlan(lookup);
        toast.success(`Plano alterado para Basiq ${PLAN_META[r.plan].name}.`);
        await refresh();
      } else {
        const { checkout_url } = await createCheckout(lookup);
        window.location.href = checkout_url;
        return;
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((d && (d.message || (typeof d === "string" ? d : null))) || "Não foi possível alterar o plano.");
      if (d?.code === "no_subscription") await refresh();
    }
    setBusy("");
  };

  const resume = async () => {
    setBusy("resume");
    try { await resumeSubscription(); toast.success("Assinatura reativada!"); await refresh(); }
    catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d?.message || "Não foi possível reativar agora.");
      if (d?.code === "no_subscription") await refresh();
    }
    setBusy("");
  };

  const q = usage?.quotes_this_month ?? 0;
  const qLim = limits?.quotes_per_month;

  return (
    <div className="max-w-4xl mx-auto px-5 pt-24 pb-20 float-up" data-testid="subscription-page">
      <button onClick={onBack} data-testid="subscription-back" className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 transition-colors">
        <ArrowLeft size={15} /> Voltar
      </button>
      <h1 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Assinatura</h1>
      <p className="mt-1 text-[14px] text-slate-500">Gerencie seu plano, ciclo de cobrança e uso.</p>

      <section className="mt-6 bg-white ring-1 ring-slate-200 rounded-3xl p-6 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.2)]" data-testid="current-plan-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Plano atual</span>
              <span data-testid="current-plan-badge" className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${plan === "free" ? "bg-slate-100 text-slate-500" : "bg-slate-900 text-white"}`}>
                {plan !== "free" && <Crown size={10} />}{PLAN_META[plan]?.badge}
              </span>
            </div>
            <div className="mt-1 text-xl font-bold tracking-tight text-slate-900">Basiq {PLAN_META[plan]?.name}</div>
            <div className="mt-1 text-[13px] text-slate-500" data-testid="subscription-status-line">
              {statusLine}{plan !== "free" && currentLookup ? ` Cobrança ${currentLookup.endsWith("yearly") ? "anual" : "mensal"}.` : ""}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-right">
            <Stat label="Orçamentos no mês" value={qLim == null ? `${q}` : `${q}/${qLim}`} testid="usage-quotes" />
            <Stat label="Empresas" value={limits?.companies == null ? `${usage?.companies ?? 0}` : `${usage?.companies ?? 0}/${limits.companies}`} testid="usage-companies" />
          </div>
        </div>

        {sub.cancel_at_period_end && hasSub && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3" data-testid="cancel-scheduled-banner">
            <p className="text-[13px] text-amber-800">Cancelamento agendado. Mudou de ideia? Você pode reativar sem custo.</p>
            <button onClick={resume} disabled={!!busy} data-testid="resume-subscription-btn" className="h-9 px-4 rounded-full bg-slate-900 text-white text-[13px] font-semibold inline-flex items-center gap-2 hover:bg-slate-800 disabled:opacity-60 transition-colors">
              {busy === "resume" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Reativar assinatura
            </button>
          </div>
        )}
      </section>

      <section className="mt-10">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-slate-900">Planos</h2>
            <p className="text-[13px] text-slate-500">{hasSub ? "Ao trocar, o plano atual é encerrado e o novo é cobrado integralmente agora, iniciando um novo ciclo." : "Assine com 7 dias grátis."}</p>
          </div>
          <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="billing-toggle">
            <button onClick={() => setAnnual(false)} data-testid="billing-monthly" className={`text-[12px] font-medium px-3 py-1 rounded-full transition-all ${!annual ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>Mensal</button>
            <button onClick={() => setAnnual(true)} data-testid="billing-yearly" className={`text-[12px] font-medium px-3 py-1 rounded-full transition-all inline-flex items-center gap-1.5 ${annual ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
              Anual <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">-2 meses</span>
            </button>
          </div>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          {["free", "standard", "pro"].map((p) => {
            const meta = PLAN_META[p];
            const lookup = p === "free" ? null : annual ? meta.yearly_lookup : meta.monthly_lookup;
            const isCurrent = p === "free" ? plan === "free" : hasSub && currentLookup === lookup;
            const samePlanOtherCycle = p !== "free" && hasSub && plan === p && !isCurrent;
            const price = p === "free" ? "0" : annual ? meta.yearly : meta.monthly;
            return (
              <div key={p} data-testid={`plan-card-${p}`} className={`relative rounded-3xl p-5 ring-1 bg-white flex flex-col transition-all ${isCurrent ? "ring-2 ring-slate-900" : "ring-slate-200 hover:ring-slate-300"}`}>
                {isCurrent && <span className="absolute -top-2.5 left-5 text-[10px] font-semibold text-white bg-slate-900 rounded-full px-2 py-0.5">Plano atual</span>}
                <div className="text-[15px] font-bold text-slate-900">Basiq {meta.name}</div>
                <div className="mt-2 leading-none">
                  <span className="text-[26px] font-extrabold text-slate-900">R${price}</span>
                  <span className="text-[12px] text-slate-400">/mês</span>
                </div>
                <div className="mt-4 space-y-1.5 flex-1">
                  {FEATURES[p].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-[12.5px] text-slate-600">
                      <Check size={13} strokeWidth={3} className="text-slate-900 shrink-0" /> {f}
                    </div>
                  ))}
                </div>
                {p === "free" ? (
                  <div className="mt-5 h-10 grid place-items-center text-[12.5px] text-slate-400">{plan === "free" ? "Você está aqui" : "Plano gratuito"}</div>
                ) : (
                  <button onClick={() => choose(p)} disabled={isCurrent || !!busy} data-testid={`plan-select-${p}`}
                    className={`mt-5 h-10 rounded-full text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-60 disabled:active:scale-100 ${isCurrent ? "bg-slate-100 text-slate-500" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
                    {busy === lookup ? <Loader2 size={15} className="animate-spin" /> : isCurrent ? "Plano atual" : hasSub ? (samePlanOtherCycle ? `Mudar para ${annual ? "anual" : "mensal"}` : `Mudar para ${meta.name}`) : <><Zap size={14} /> Testar 7 dias grátis</>}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {hasSub && !sub.cancel_at_period_end && (
        <p className="mt-14 text-center text-[12px] text-slate-400">
          Precisa parar por aqui?{" "}
          <button onClick={() => setCancelOpen(true)} data-testid="cancel-subscription-link" className="underline underline-offset-2 hover:text-slate-600 transition-colors">
            Cancelar assinatura
          </button>
        </p>
      )}

      {cancelOpen && (
        <CancelSubscriptionModal periodEnd={fmtDate(sub.current_period_end || sub.trial_end)} onClose={() => setCancelOpen(false)} onDone={async () => { setCancelOpen(false); await refresh(); }} />
      )}
    </div>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl bg-slate-50 px-4 py-3 text-left min-w-[120px]">
    <div className="text-[11px] text-slate-400">{label}</div>
    <div className="text-[17px] font-bold text-slate-900" data-testid={testid}>{value}</div>
  </div>
);
